import { useState, useEffect } from 'react';
import { apiGet, apiPut } from '@/lib/api-client';

interface AIConfigSettingsProps {
  isSuperAdmin: boolean;
}

export function AIConfigSettings({ isSuperAdmin }: AIConfigSettingsProps) {
  const [systemPrompt, setSystemPrompt] = useState('');
  const [provider, setProvider] = useState<'openai' | 'anthropic' | 'google'>('google');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const modelOptions = {
    openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'],
    anthropic: ['claude-sonnet-4-5-20250929', 'claude-haiku-4-5-20251001'],
    google: ['gemini-2.0-flash', 'gemini-1.5-pro-latest', 'gemini-1.5-flash-latest'],
  };

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      setLoading(true);

      const data = await apiGet<Record<string, any>>(
        '/api/admin/settings?keys=ai.system_prompt,ai.provider,ai.model_name,ai.temperature'
      );

      if (data['ai.system_prompt']) {
        setSystemPrompt(data['ai.system_prompt']?.prompt || '');
      }
      if (data['ai.provider']) {
        setProvider(data['ai.provider']?.provider || 'google');
      }
      if (data['ai.model_name']) {
        setModel(data['ai.model_name']?.model || '');
      }
      if (data['ai.temperature']) {
        setTemperature(data['ai.temperature']?.temperature || 0.7);
      }
    } catch (err) {
      console.error('Error loading AI settings:', err);
      setMessage({ type: 'error', text: 'Failed to load AI settings' });
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      setMessage(null);

      const settings: Record<string, any> = {
        'ai.system_prompt': { prompt: systemPrompt },
        'ai.provider': { provider },
        'ai.model_name': { model },
        'ai.temperature': { temperature },
      };

      await apiPut('/api/admin/settings', { settings });

      setMessage({ type: 'success', text: 'AI configuration saved successfully!' });
    } catch (err) {
      console.error('Error saving AI settings:', err);
      setMessage({ type: 'error', text: 'Failed to save AI settings' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-2">AI Configuration</h2>
        <p className="text-slate-400 text-sm">Configure the AI coaching system and master prompt</p>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* LLM Provider Selection */}
      <div className="glass-strong p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">LLM Provider</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Select Provider</label>
            <select
              value={provider}
              onChange={(e) => {
                const newProvider = e.target.value as typeof provider;
                setProvider(newProvider);
                setModel(modelOptions[newProvider][0] || ''); // Reset to first model
              }}
              disabled={!isSuperAdmin}
              className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                       focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="openai">OpenAI (GPT-4, GPT-3.5)</option>
              <option value="anthropic">Anthropic (Claude 3.5)</option>
              <option value="google">Google (Gemini 1.5)</option>
            </select>
            {!isSuperAdmin && (
              <p className="text-xs text-amber-400 mt-1">Only super admins can change the provider</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Model</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                       focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
            >
              {modelOptions[provider].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Temperature: {temperature}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={temperature}
              onChange={(e) => setTemperature(parseFloat(e.target.value))}
              className="w-full accent-amber-500"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>More focused</span>
              <span>More creative</span>
            </div>
          </div>
        </div>
      </div>

      {/* System Prompt */}
      <div className="glass-strong p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Master System Prompt</h3>
        <p className="text-sm text-slate-400 mb-4">
          This prompt defines the AI coach's personality, behavior, and coaching approach.
        </p>

        <textarea
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          rows={12}
          placeholder="You are LevelUp Coach, an empathetic coach..."
          className="w-full px-4 py-3 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                   font-mono text-sm resize-none
                   focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                   placeholder:text-slate-600"
        />

        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-slate-500">{systemPrompt.length} characters</p>
          <p className="text-xs text-slate-500">~{Math.ceil(systemPrompt.length / 4)} tokens</p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-royal-gradient text-white font-medium rounded-xl
                   hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed
                   shadow-glow flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
}
